'use client';

import { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import Icon from '@/components/ui/AppIcon';
import Sidebar from '@/components/common/Sidebar';
import { AnthropicMessage } from '@/lib/types/anthropic';

type ConversationId = 'kin' | 'family' | 'doctor';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const SYSTEM_PROMPT = `You are a helpful benefits navigator assistant for Kin, a family caregiving coordination platform. 

Your role is to help families understand and navigate:
- Medicare and Medicaid benefits
- Veterans benefits and VA programs
- Social Security benefits (SSI, SSDI)
- Long-term care insurance
- State and local assistance programs
- Caregiver support programs
- Medical equipment and supplies coverage
- Prescription drug assistance programs
- Housing and utility assistance
- Transportation services

Provide clear, actionable guidance. Ask clarifying questions when needed. Be empathetic and supportive, understanding that navigating benefits can be overwhelming for family caregivers.`;

const SUGGESTED_QUESTIONS = [
  "What Medicare benefits are available for home health care?",
  "How do I apply for Medicaid for my elderly parent?",
  "What caregiver support programs can help with respite care?",
  "Are there benefits to help cover medical equipment costs?",
];

const FAMILY_MESSAGES = [
  { from: 'Sarah', content: "Mom's visual assessment came back 96/100 today 🎉", isYou: true },
  { from: 'Michael', content: "That's great! I'll grab her Metformin refill tomorrow", isYou: false },
  { from: 'Jennifer', content: 'I can take her to the Thursday appointment', isYou: false },
  { from: 'Sarah', content: 'Perfect. Kin flagged her pain is trending up though', isYou: true },
  { from: 'Michael', content: 'Should we call Dr. Chen?', isYou: false },
];

const DOCTOR_MESSAGES = [
  { from: 'Dr. Chen', content: "I reviewed Margaret's latest Kin report. Her symmetry scores look stable.", isYou: false },
  { from: 'Sarah', content: "Her pain has been trending up this week, should we be concerned?", isYou: true },
  { from: 'Dr. Chen', content: "Given her Metformin and Lisinopril combination I'd like to see her within 2 weeks. Can you book through the portal?", isYou: false },
  { from: 'Sarah', content: "Yes I'll book today. Should we adjust her medication schedule?", isYou: true },
  { from: 'Dr. Chen', content: "Let's discuss in the appointment. Keep monitoring with Kin daily.", isYou: false },
];

export default function BenefitsNavigatorPage() {
  const [activeConversation, setActiveConversation] = useState<ConversationId>('kin');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [familyInput, setFamilyInput] = useState('');
  const [doctorInput, setDoctorInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || input;
    if (!textToSend.trim() || isStreaming) return;

    setError(null);
    setInput('');

    const userMessage: Message = { role: 'user', content: textToSend };
    setMessages((prev) => [...prev, userMessage]);
    setIsStreaming(true);
    abortControllerRef.current = new AbortController();

    try {
      const conversationHistory: AnthropicMessage[] = messages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      const response = await fetch('/api/anthropic/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: textToSend,
          conversationHistory,
          system: SYSTEM_PROMPT,
          temperature: 0.7,
          max_tokens: 2048,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error?.message || 'Failed to get response');
        setIsStreaming(false);
        return;
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) {
        setError('Failed to read response stream');
        setIsStreaming(false);
        return;
      }

      let assistantMessage = '';
      setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            try {
              const parsed = JSON.parse(data);
              if (parsed.type === 'text') {
                assistantMessage += parsed.content;
                setMessages((prev) => {
                  const newMessages = [...prev];
                  newMessages[newMessages.length - 1] = { role: 'assistant', content: assistantMessage };
                  return newMessages;
                });
              } else if (parsed.type === 'done') break;
              else if (parsed.type === 'error') {
                setError(parsed.content);
                break;
              }
            } catch {
              continue;
            }
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Network error occurred.');
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    handleSendMessage(question);
  };

  const handleClearChat = () => {
    setMessages([]);
    setError(null);
  };

  const conversations = [
    { id: 'kin' as const, label: 'Kin Assistant', emoji: '🤖', preview: messages.length ? (messages[messages.length - 1].content?.slice(0, 35) + '…') : 'Ask about benefits...' },
    { id: 'family' as const, label: 'Chen Family', emoji: '👨‍👩‍👧', preview: "Should we call Dr. Chen?" },
    { id: 'doctor' as const, label: 'Dr. Robert Chen', emoji: '👨‍⚕️', preview: "Keep monitoring with Kin daily." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <div className="pt-20 pb-[88px] lg:pb-8 max-w-[960px] mx-auto px-6 w-full min-h-screen flex flex-col">
        <div className="flex flex-1 min-h-0">
          {/* Left panel - conversations */}
          <aside className="w-full lg:w-72 border-r border-sage-200/30 glass flex-shrink-0 flex flex-col">
            <div className="p-4 border-b border-sage-200/30">
              <h1 className="text-xl font-heading font-bold text-foreground">Kin Chat</h1>
            </div>
            <nav className="flex-1 overflow-y-auto p-2">
              {conversations.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setActiveConversation(c.id)}
                  className={`w-full text-left p-3 rounded-lg mb-1 transition-smooth ${
                    activeConversation === c.id ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-foreground'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{c.emoji}</span>
                    <span className="font-medium">{c.label}</span>
                  </div>
                  <p className="text-sm text-muted-foreground truncate mt-0.5">{c.preview}</p>
                </button>
              ))}
            </nav>
          </aside>

          {/* Right panel - active conversation */}
          <main className="flex-1 flex flex-col min-w-0">
            {/* Kin Assistant - unchanged Claude chat */}
            {activeConversation === 'kin' && (
              <>
                <div className="glass border-b border-border px-4 lg:px-6 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🤖</span>
                    <span className="font-heading font-semibold text-foreground">Kin Assistant</span>
                  </div>
                  {messages.length > 0 && (
                    <button
                      onClick={handleClearChat}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg"
                    >
                      <Icon name="TrashIcon" size={14} />
                      Clear
                    </button>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto px-4 lg:px-6 py-4">
                  <div className="max-w-4xl mx-auto space-y-6">
                    {messages.length === 0 && (
                      <div className="text-center py-8">
                        <div className="inline-flex p-4 bg-primary/10 rounded-full mb-4">
                          <Icon name="SparklesIcon" size={32} className="text-primary" />
                        </div>
                        <h2 className="text-xl font-heading font-bold text-foreground mb-2">Welcome to Kin Assistant</h2>
                        <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                          Ask me anything about Medicare, Medicaid, Veterans benefits, caregiver support programs, and more.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl mx-auto">
                          {SUGGESTED_QUESTIONS.map((q, i) => (
                            <button
                              key={i}
                              onClick={() => handleSuggestedQuestion(q)}
                              className="p-4 glass border border-border rounded-lg text-left hover:border-primary hover:bg-primary/5 transition-smooth group"
                            >
                              <div className="flex items-start gap-3">
                                <Icon name="ChatBubbleLeftRightIcon" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-foreground group-hover:text-primary transition-smooth">{q}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {messages.map((message, index) => (
                      <div key={index} className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {message.role === 'assistant' && (
                          <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                            <Icon name="SparklesIcon" size={16} className="text-primary" />
                          </div>
                        )}
                        <div
                          className={`max-w-3xl px-4 py-3 rounded-lg ${
                            message.role === 'user' ? 'bg-primary text-white' : 'glass border border-border text-foreground'
                          }`}
                        >
                          <div className="text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none">
                            <ReactMarkdown>{message.content}</ReactMarkdown>
                          </div>
                        </div>
                        {message.role === 'user' && (
                          <div className="flex-shrink-0 w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                            <Icon name="UserIcon" size={16} className="text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    ))}

                    {isStreaming && messages[messages.length - 1]?.content === '' && (
                      <div className="flex gap-4 justify-start">
                        <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <Icon name="SparklesIcon" size={16} className="text-primary" />
                        </div>
                        <div className="max-w-3xl px-4 py-3 rounded-lg glass border border-border">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <div className="animate-pulse">●</div>
                            <div className="animate-pulse animation-delay-200">●</div>
                            <div className="animate-pulse animation-delay-400">●</div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </div>

                {error && (
                  <div className="px-4 lg:px-6 pb-2">
                    <div className="max-w-4xl mx-auto flex items-start gap-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                      <Icon name="ExclamationTriangleIcon" size={18} className="text-destructive flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-destructive flex-1">{error}</p>
                      <button onClick={() => setError(null)} className="text-destructive hover:text-destructive/80">
                        <Icon name="XMarkIcon" size={18} />
                      </button>
                    </div>
                  </div>
                )}

                <div className="glass border-t border-border px-4 lg:px-6 py-3">
                  <div className="max-w-4xl mx-auto flex gap-3">
                    <input
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                      placeholder="Ask about benefits, programs, or eligibility..."
                      className="flex-1 px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground placeholder:text-muted-foreground"
                      disabled={isStreaming}
                    />
                    <button
                      onClick={() => handleSendMessage()}
                      disabled={isStreaming || !input.trim()}
                      className="px-5 py-3 bg-primary text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 font-medium flex items-center gap-2"
                    >
                      {isStreaming ? (
                        <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Thinking...</>
                      ) : (
                        <><Icon name="PaperAirplaneIcon" size={18} />Send</>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Chen Family - group chat */}
            {activeConversation === 'family' && (
              <>
                <div className="glass border-b border-border px-4 lg:px-6 py-3 flex items-center gap-2">
                  <span className="text-lg">👨‍👩‍👧</span>
                  <span className="font-heading font-semibold text-foreground">Chen Family</span>
                </div>
                <div className="flex-1 overflow-y-auto px-4 lg:px-6 py-4 bg-muted/20">
                  {FAMILY_MESSAGES.map((msg, i) => (
                    <div key={i} className={`flex ${msg.isYou ? 'justify-end' : 'justify-start'} mb-3`}>
                      <div className={`max-w-[85%] rounded-lg px-4 py-2 ${msg.isYou ? 'bg-primary text-white' : 'glass border border-sage-200/30'}`}>
                        <p className="text-xs font-semibold text-primary mb-0.5">{msg.from}</p>
                        <p className="text-sm">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="glass border-t border-border px-4 lg:px-6 py-3">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={familyInput}
                      onChange={(e) => setFamilyInput(e.target.value)}
                      placeholder="Message family..."
                      className="flex-1 px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground placeholder:text-muted-foreground"
                    />
                    <button className="px-5 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 font-medium">
                      Send
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Dr. Robert Chen - DM */}
            {activeConversation === 'doctor' && (
              <>
                <div className="glass border-b border-border px-4 lg:px-6 py-3 flex items-center gap-2">
                  <span className="text-lg">👨‍⚕️</span>
                  <span className="font-heading font-semibold text-foreground">Dr. Robert Chen</span>
                </div>
                <div className="flex-1 overflow-y-auto px-4 lg:px-6 py-4 bg-muted/20">
                  {DOCTOR_MESSAGES.map((msg, i) => (
                    <div key={i} className={`flex ${msg.isYou ? 'justify-end' : 'justify-start'} mb-3`}>
                      <div className={`max-w-[85%] rounded-lg px-4 py-2 ${msg.isYou ? 'bg-primary text-white' : 'glass border border-sage-200/30'}`}>
                        <p className="text-xs font-semibold text-primary mb-0.5">{msg.from}</p>
                        <p className="text-sm">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="glass border-t border-border px-4 lg:px-6 py-3">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={doctorInput}
                      onChange={(e) => setDoctorInput(e.target.value)}
                      placeholder="Message Dr. Chen..."
                      className="flex-1 px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground placeholder:text-muted-foreground"
                    />
                    <button className="px-5 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 font-medium">
                      Send
                    </button>
                  </div>
                </div>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
